<script setup lang="ts">
import { UiIcon, UiIconButton, UiText } from '@shopexperts/shared';
import type { PageSchemaField } from '@shopexperts/shared';
import { getPageFieldTypeDefinition } from '../../field-types';
import MetafieldKeyCopyButton from '../shared/MetafieldKeyCopyButton.vue';

const props = defineProps<{
  fields: PageSchemaField[];
  selectedIndex: number;
}>();

const emit = defineEmits<{
  selectField: [index: number];
  addField: [];
  duplicateField: [index: number];
  deleteField: [index: number];
  moveField: [index: number, direction: -1 | 1];
}>();
</script>

<template>
  <div class="border-y border-se-gray-200 bg-white">
    <div class="schema-field-row hidden items-center gap-3 border-b border-se-gray-200 px-3 py-2 md:grid">
      <span />
      <UiText as="span" variant="caption-normal" class="font-semibold uppercase text-se-gray-700" text="Field" />
      <UiText as="span" variant="caption-normal" class="font-semibold uppercase text-se-gray-700" text="Type" />
      <UiText as="span" variant="caption-normal" class="font-semibold uppercase text-se-gray-700" text="Required" />
      <UiText as="span" variant="caption-normal" class="text-right font-semibold uppercase text-se-gray-700" text="Actions" />
    </div>

    <div
      v-for="(field, index) in props.fields"
      :key="`${field.key}:${index}`"
      role="button"
      tabindex="0"
      class="schema-field-row w-full items-center gap-3 border-b border-se-gray-200 px-3 py-2.5 text-left last:border-b-0 hover:bg-se-gray-50"
      :class="{ 'selected-schema-field bg-se-gray-100': index === props.selectedIndex }"
      @click="emit('selectField', index)"
      @keydown.enter.prevent="emit('selectField', index)"
      @keydown.space.prevent="emit('selectField', index)"
    >
      <span class="flex h-8 w-8 items-center justify-center rounded-md bg-se-gray-100">
        <UiIcon :name="getPageFieldTypeDefinition(field.type).icon" class="h-4 w-4 text-se-gray-700" />
      </span>
      <span class="flex min-w-0 items-center gap-2">
        <UiText as="span" variant="p-small" class="truncate text-se-dark" :text="field.label" />
        <MetafieldKeyCopyButton :field-key="field.key" />
      </span>
      <UiText as="span" variant="caption-normal" class="truncate text-se-gray-700" :text="getPageFieldTypeDefinition(field.type).label" />
      <UiText as="span" variant="caption-normal" :text="field.required ? 'Required' : '-'" />
      <span class="flex justify-end gap-1">
        <UiIconButton icon="arrow-narrow-left" aria-label="Move field up" variant="ghost" class="h-7 w-7 rotate-90" @click.stop="emit('moveField', index, -1)" />
        <UiIconButton icon="arrow-narrow-left" aria-label="Move field down" variant="ghost" class="h-7 w-7 -rotate-90" @click.stop="emit('moveField', index, 1)" />
        <UiIconButton icon="copy" aria-label="Duplicate field" variant="ghost" class="h-7 w-7" @click.stop="emit('duplicateField', index)" />
        <UiIconButton icon="delete" aria-label="Delete field" variant="ghost" class="h-7 w-7" @click.stop="emit('deleteField', index)" />
      </span>
    </div>
    <button type="button" class="flex w-full items-center gap-2 px-3 py-4 text-left hover:bg-se-gray-50" @click="emit('addField')">
      <UiIcon name="plus" class="h-5 w-5 text-se-gray-700" />
      <UiText as="span" variant="p-small" text="Add field" />
    </button>
  </div>
</template>

<style scoped>
.schema-field-row {
  display: grid;
  grid-template-columns: 36px minmax(180px, 1fr) minmax(132px, 168px) 92px 112px;
}

@media (max-width: 1280px) {
  .schema-field-row {
    grid-template-columns: 36px minmax(160px, 1fr) minmax(112px, 140px) 96px;
  }

  .schema-field-row > :nth-child(4) {
    display: none;
  }
}

.selected-schema-field {
  box-shadow: inset 2px 0 0 var(--color-se-dark);
}
</style>
